/**
 * The re-verification a person does before an act ProDoc cannot take back —
 * a link anyone can open (D26), a retention override (D24), a DPDP erasure
 * request (D25). `prodoc-service` gates each with `require_stepup()`, and this
 * is how the browser gets the assertion it checks.
 *
 * Copy-adapted from iTax (`repo:itax:apps/web/src/components/StepUpDialog.tsx`,
 * itself ProDesk's). Two steps and one rule: **the assertion is handed to the
 * caller and never kept.** The dialog says *what* is about to happen, because
 * "confirm your identity" with no object is a prompt people learn to click
 * through. Locally the core stub accepts `000000`.
 *
 * `StepUpProvider` / `useStepUp()` turn the dialog into the package's
 * `StepUpAsk` (`lib/prodoc-client.ts`): a screen asks, the person
 * re-verifies, and the promise resolves with the assertion for the one request
 * — or rejects with `StepUpCancelled` when they close it. The package's
 * components take that function as a prop and never import this file
 * (FR-35); the host owns the session the challenge rides on.
 */

import { ApiError, StepUpCancelled, type StepUpAsk } from '@fiveox/prodoc-ui'
import { Alert, Button, Dialog, OTPInput } from '@fiveox/ui'
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

import { AUTH_API, SSO_CLIENT_ID } from '../lib/config'
import { session } from '../lib/session'
import { METHOD_LABELS, STEPUP_METHODS, createStepUpApi, type StepUpApi, type StepUpMethod } from '../lib/stepup'

/** core-auth over this app's session, built on first use (the session store is the shell's). */
let defaultApi: StepUpApi | null = null
const appStepUpApi = (): StepUpApi => (defaultApi ??= createStepUpApi({ authApiUrl: AUTH_API, clientId: SSO_CLIENT_ID, fetcher: session.authed }))

export function StepUpDialog({
  open,
  title,
  what,
  onClose,
  onVerified,
  api,
}: {
  open: boolean
  title: string
  /** One sentence naming the irreversible thing. */
  what: string
  onClose: () => void
  /** Receives the assertion for the one request it was minted for. */
  onVerified: (assertion: string) => Promise<void> | void
  /** core-auth, or a stand-in for a test. */
  api?: StepUpApi
}) {
  const [method, setMethod] = useState<StepUpMethod>('email')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const stepUp = api ?? appStepUpApi()

  const reset = () => {
    setSent(false)
    setCode('')
    setError(null)
    setBusy(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  const send = async (chosen: StepUpMethod) => {
    setBusy(true)
    setError(null)
    setMethod(chosen)
    try {
      if (chosen !== 'totp') await stepUp.sendCode(chosen)
      setSent(true)
    } catch (err) {
      // Core's own words: a person may simply not hold this factor.
      setError(err instanceof ApiError ? err.message : 'That code could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  const verify = async () => {
    setBusy(true)
    setError(null)
    try {
      const assertion = await stepUp.assertStepUp(method, code)
      reset()
      await onVerified(assertion)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code was not accepted.')
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} title={title}>
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">{what}</p>
        {error && <Alert tone="error">{error}</Alert>}

        {!sent ? (
          <div className="space-y-2">
            <p className="text-sm">How would you like to confirm it is you?</p>
            {STEPUP_METHODS.map((option) => (
              <Button key={option} type="button" variant="secondary" className="w-full justify-start" disabled={busy} onClick={() => void send(option)}>
                {METHOD_LABELS[option]}
              </Button>
            ))}
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (code.length >= 6 && !busy) void verify()
            }}
          >
            <p className="text-sm">{method === 'totp' ? 'Enter the current code from your authenticator app.' : 'Enter the six-digit code we just sent you.'}</p>
            <OTPInput value={code} onChange={setCode} autoFocus disabled={busy} />
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={busy || code.length < 6} loading={busy} loadingText="Checking…">
                Confirm
              </Button>
              <Button type="button" variant="ghost" onClick={reset} disabled={busy}>
                Use a different method
              </Button>
            </div>
          </form>
        )}
      </div>
    </Dialog>
  )
}

/* -------------------------------------------------- the ask, for a screen */

interface Pending {
  title: string
  what: string
  resolve: (assertion: string) => void
  reject: (reason: unknown) => void
}

const StepUpContext = createContext<StepUpAsk | null>(null)

/**
 * Mounted once by the shell around the screens: renders the dialog when a
 * screen asks, and settles that screen's promise. A second ask while one is
 * open cancels the first — one re-verification answers one request.
 */
export function StepUpProvider({ children, api }: { children: ReactNode; api?: StepUpApi }) {
  const [pending, setPending] = useState<Pending | null>(null)
  const current = useRef<Pending | null>(null)

  const ask = useCallback<StepUpAsk>(
    (request) =>
      new Promise<string>((resolve, reject) => {
        current.current?.reject(new StepUpCancelled())
        const next = { ...request, resolve, reject }
        current.current = next
        setPending(next)
      }),
    [],
  )

  const settle = (fn: (p: Pending) => void) => {
    const p = current.current
    current.current = null
    setPending(null)
    if (p) fn(p)
  }

  return (
    <StepUpContext.Provider value={ask}>
      {children}
      <StepUpDialog
        open={pending !== null}
        title={pending?.title ?? 'Confirm it is you'}
        what={pending?.what ?? ''}
        api={api}
        onClose={() => settle((p) => p.reject(new StepUpCancelled()))}
        // Handed straight on and dropped: nothing here holds it after this line.
        onVerified={(assertion) => settle((p) => p.resolve(assertion))}
      />
    </StepUpContext.Provider>
  )
}

/** The host's `StepUpAsk` for the package's components. Outside a provider it refuses, so nothing is sent unverified. */
export function useStepUp(): StepUpAsk {
  const ask = useContext(StepUpContext)
  return useMemo<StepUpAsk>(() => ask ?? (() => Promise.reject(new StepUpCancelled())), [ask])
}
